import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { useParams } from 'react-router-dom';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import QRCodeStyling from 'qr-code-styling';
import { useQRCodeContext } from 'contexts/QrCodeContext';

const TeamEventBadgesRight = ({ loading, cardDetails, checkedItems, callToActionText, layout, setDownloadContent, contentRef, cardId: propCardId }) => {
    console.log('TeamEventBadgesRight rendered', { checkedItems });

    const [name, setName] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [company, setCompany] = useState('');
    const [location, setLocation] = useState('');
    const [callToAction, setCallToAction] = useState('');
    const [profilePhoto, setProfilePhoto] = useState('');
    const [imageLoaded, setImageLoaded] = useState(false);
    const { cardId: paramCardId } = useParams();
    const cardId = propCardId || paramCardId;
    const qrContainerRef = useRef(null);
    const avatarRef = useRef(null);
    const qrRef = useRef(null);
    const { qrCodeState } = useQRCodeContext();

    const BASE_URL = `${FRONTEND_BASE_URL}/card`;
    const cardUrl = `${BASE_URL}/${cardId}?source=event_badge&ts=${Date.now()}`;

    // Create a key that changes whenever checkedItems change
    const renderKey = useMemo(() => {
        return Object.entries(checkedItems)
            .map(([key, value]) => `${key}:${value}`)
            .join('|');
    }, [checkedItems]);

    useEffect(() => {
        console.log('Card details effect', cardDetails);
        if (cardDetails.card) {
            setName(cardDetails.card.contact_info?.fullName || '');
            setJobTitle(cardDetails.card.contact_info?.jobTitle || '');
            setCompany(cardDetails.card.contact_info?.company || '');
            setLocation(cardDetails.card.contact_info?.location || '');
            setCallToAction(cardDetails.card.callToAction || '');
            setProfilePhoto(cardDetails.card.profilePictureUrl || '');
            // Reset image loaded state when photo URL changes
            setImageLoaded(false);
        }
    }, [cardDetails]);

    useEffect(() => {
        console.log('Download content effect');
        if (contentRef.current) {
            setDownloadContent(contentRef.current.innerHTML);
        }
    }, [setDownloadContent, renderKey, contentRef, imageLoaded]);

    useEffect(() => {
        console.log('QR code effect triggered', { checkedItems, qrCodeState });
        const initializeQR = async () => {
            if (qrContainerRef.current && checkedItems.qrCode) {
                qrContainerRef.current.innerHTML = '';

                const contextQrCodeColor = qrCodeState[cardId]?.contextQrCodeColor || '#000000';
                const contextQrCodeLogoUrl = checkedItems.qrCodeLogo ? qrCodeState[cardId]?.contextQrCodeLogoUrl : null;

                console.log('QR code options', { contextQrCodeColor, contextQrCodeLogoUrl });

                qrRef.current = new QRCodeStyling({
                    width: 200,
                    height: 200,
                    type: "svg",
                    data: cardUrl,
                    image: contextQrCodeLogoUrl,
                    dotsOptions: {
                        color: contextQrCodeColor,
                        type: "square"
                    },
                    cornersSquareOptions: {
                        type: "square"
                    },
                    cornersDotOptions: {
                        type: "square"
                    },
                    backgroundOptions: {
                        color: "#ffffff"
                    },
                    imageOptions: {
                        crossOrigin: "anonymous",
                        margin: 7,
                        imageSize: 0.5,
                        hideBackgroundDots: true,
                    },
                    qrOptions: {
                        errorCorrectionLevel: "H"
                    },
                });

                await qrRef.current.append(qrContainerRef.current);
            }
        };

        initializeQR();
    }, [cardUrl, qrCodeState, cardId, checkedItems.qrCode, checkedItems.qrCodeLogo, renderKey]);

    // Handle image load for download/print capturing
    const handleImageLoad = () => {
        setImageLoaded(true);
        console.log('Profile image loaded successfully');
    };

    // Handle image error
    const handleImageError = () => {
        console.error('Error loading profile image');
        setImageLoaded(true); // Consider it loaded even if there's an error, to prevent blocking
    };

    if (loading) {
        return <Typography>Loading...</Typography>;
    }

    if (!cardDetails.card) {
        return <Typography>No card details available.</Typography>;
    }

    const layoutStyles = {
        badge: { width: '450px', height: '550px', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
        nametag: { width: '400px', height: '300px', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex' },
        square: { width: '550px', height: '550px', border: '1px solid #e0e0e0', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
    };

    return (
        <Box
            key={renderKey}
            sx={{
                flex: 1,
                padding: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
            }}
        >
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                Preview
            </Typography>

            <div ref={contentRef} style={layoutStyles[layout]}>
                <Box sx={layoutStyles[layout]}>
                    {/* Profile Section */}
                    <Box
                        sx={{
                            padding: 2,
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {checkedItems.profilePhoto && (
                            <div style={{
                                width: '115px',
                                height: '115px',
                                borderRadius: '50%',
                                backgroundColor: '#ffffff',
                                border: '8px solid #ffffff',
                                marginBottom: '8px',
                                overflow: 'hidden',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative'
                            }}>
                                <img
                                    ref={avatarRef}
                                    src={profilePhoto || avatarPlaceholder}
                                    alt="Profile"
                                    crossOrigin="anonymous"
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                                {/* Hidden backup avatar for download capability */}
                                <div style={{ display: 'none' }}>
                                    <Avatar
                                        src={profilePhoto || avatarPlaceholder}
                                        sx={{
                                            width: '115px',
                                            height: '115px',
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        <Box>
                            {checkedItems.name && (
                                <Typography sx={{ fontSize: '24px', fontWeight: 'bold', color: 'black' }}>
                                    {name}
                                </Typography>
                            )}
                            {checkedItems.jobTitle && (
                                <Typography sx={{ fontSize: '16px', fontWeight: 'medium', color: 'black' }}>
                                    {jobTitle}
                                </Typography>
                            )}
                            {checkedItems.company && (
                                <Typography sx={{ fontSize: '14px', fontWeight: 'normal', color: 'black' }}>
                                    {company}
                                </Typography>
                            )}
                            {checkedItems.location && (
                                <Typography sx={{ fontSize: '14px', fontWeight: 'normal', color: 'grey' }}>
                                    {location}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* QR Code Section */}
                    <Box
                        sx={{
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {checkedItems.qrCode && (
                            <Box
                                ref={qrContainerRef}
                                sx={{
                                    width: 200,
                                    height: 200,
                                }}
                            />
                        )}

                        {checkedItems.callToAction && (
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', fontSize: '24px', color: 'black' }}>
                                {callToActionText}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </div>
        </Box>
    );
};

export default TeamEventBadgesRight;